import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Shield, AlertCircle } from 'lucide-react';

const AdminLogin = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      // Google OAuth flow
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback
      });

      window.google.accounts.id.prompt();
    } catch (error) {
      console.error('Google login error:', error);
      setError('Failed to initialize Google login');
      setLoading(false);
    }
  };

  const handleGoogleCallback = async (response) => {
    try {
      // Send the Google token to our backend for verification
      const authResponse = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: response.credential
        })
      });

      if (authResponse.ok) {
        const data = await authResponse.json();
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        onLoginSuccess(data.user);
      } else {
        const errorData = await authResponse.json();
        setError(errorData.detail || 'Authentication failed');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 glass-card">
        <div className="text-center mb-8">
          <Shield size={48} className="text-blue-600 mx-auto mb-4" />
          <h1 className="heading-3 text-text-primary mb-2">Admin Access</h1>
          <p className="text-text-secondary">
            Sign in with your authorized Google account to access the content management system.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <Button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full btn-base btn-primary btn-lg"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Signing in...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Sign in with Google</span>
              </div>
            )}
          </Button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-text-muted">
            Only authorized administrators can access this dashboard.
            <br />
            Contact the site owner if you need access.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;